import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Paragraph } from '@digdir/designsystemet-react';
import { SurveyActionsProvider } from '../../hooks/useSurveyActions';
import type { WorkEnvironmentSurvey } from '../../api/workEnvironmentTypes';
import { useGetWorkEnvironmentSurveyByIdQuery } from '../../hooks/queries/useGetWorkEnvironmentSurveyByIdQuery';
import { SurveyUnavailable } from './components/SurveyUnavailable';
import classes from './WorkEnvironmentSurveyDetailPage.module.css';
import { WorkEnvironmentSurveyProcess } from './WorkEnvironmentSurveyProcess';
import { followsUpRisk, isCollectingAnswers } from './surveyProcessView';
import { showsRiskPage } from './riskView';
import { WorkEnvironmentSurveyRisk } from './WorkEnvironmentSurveyRisk';
import { showsMeasuresPage } from './measuresView';
import { WorkEnvironmentSurveyMeasures } from './WorkEnvironmentSurveyMeasures';
import { showsPlanPage } from './planView';
import { WorkEnvironmentSurveyPlan } from './WorkEnvironmentSurveyPlan';
import { showsFollowUpPage } from './followUpView';
import { WorkEnvironmentSurveyFollowUp } from './WorkEnvironmentSurveyFollowUp';
import { showsEvaluationPage } from './evaluationView';
import { WorkEnvironmentSurveyEvaluation } from './WorkEnvironmentSurveyEvaluation';

export const WorkEnvironmentSurveyDetailPage = () => {
  const { t } = useTranslation();
  const { surveyId } = useParams<{ surveyId: string }>();
  const { data: survey, isLoading } = useGetWorkEnvironmentSurveyByIdQuery(Number(surveyId), {
    enabled: Boolean(surveyId),
  });

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <p>Laster...</p>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className={classes.empty}>
        <h2>Undersøkelsen ble ikke funnet</h2>
        <p>Denne undersøkelsen eksisterer ikke eller du har ikke tilgang til den.</p>
      </div>
    );
  }

  return (
    <SurveyActionsProvider actions={survey.actions}>
      {/* The team leader is the team's safety representative, so the main safety representative follows up (§ 6-2) */}
      {survey.insteadOfLeader && (
        <Alert data-color="info" className={classes.notice}>
          <Paragraph data-size="sm">{t('workEnvironmentProcess.insteadOfLeader')}</Paragraph>
        </Alert>
      )}
      {stepOf(survey)}
    </SurveyActionsProvider>
  );
};

/** The page for the step the survey is at */
const stepOf = (survey: WorkEnvironmentSurvey): ReactElement => {
  if (isCollectingAnswers(survey.status)) {
    return <WorkEnvironmentSurveyProcess survey={survey} />;
  }
  if (!followsUpRisk(survey)) {
    return (
      <div className={classes.container}>
        <SurveyUnavailable survey={survey} reason="followedUp" />
      </div>
    );
  }
  if (showsRiskPage(survey)) {
    return <WorkEnvironmentSurveyRisk survey={survey} />;
  }
  if (showsMeasuresPage(survey)) {
    return <WorkEnvironmentSurveyMeasures survey={survey} />;
  }
  if (showsPlanPage(survey)) {
    return <WorkEnvironmentSurveyPlan survey={survey} />;
  }
  if (showsFollowUpPage(survey)) {
    return <WorkEnvironmentSurveyFollowUp survey={survey} />;
  }
  if (showsEvaluationPage(survey)) {
    return <WorkEnvironmentSurveyEvaluation survey={survey} />;
  }

  return (
    <div className={classes.container}>
      <SurveyUnavailable survey={survey} />
    </div>
  );
};
